import {
    Component,
    Input,
    ViewChild,
    OnInit,
    OnDestroy,
    ViewEncapsulation
} from '@angular/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { Subscription } from 'rxjs';
import { SharedService } from '../../shared.service';
import { SpinnerService } from '../../spinner/spinner.service';
import { System } from '../../System';
import { Group, exportPDF } from '@progress/kendo-drawing';
import { PDFDocument } from 'pdf-lib';

/**
 * This component encapsulates the Kendo PDFExport.
 *
 * @input keepTogether: An optional CSS selector that specifies the elements which should not be split across the pages .
 * @input paperSize: Specifies the paper size of the PDF document (see example). Defaults to "Letter".
 * @input margin: Specifies the margins of the page.. Defaults to 1cm.
 * @input scale: A scale factor. The text size on the screen might be too big for printing. To scale down the output in PDF, use this option.
 * @input landscape: A flag that indicates if the page will be in a landscape orientation. Defaults to true.
 * @input fileName: Sets the name of the file that will be created.  Defaults to 'pdfExport.pdf'.
 *
 * NOTES:
 *
 */
@AutoUnsubscribe()
@Component({
    selector: 'tyl-kendo-pdf-export',
    templateUrl: './tyl-kendo-pdf-export.component.html',  
    // View Encapsulation must be disabled in order to style the exported content
    encapsulation: ViewEncapsulation.None,
    styleUrls: [
        './tyl-kendo-pdf-export.component.css'
    ]
})
export class TylKendoPdfExportComponent implements OnInit, OnDestroy {

    // Interface Properties for KendoPdfExport
    @Input() keepTogether: string;
    @Input() paperSize: string = 'Letter';
    @Input() margin: string = '1cm';
    @Input() scale: number = 0.50;
    @Input() landscape: boolean = true;

    // Custom Interface Properties
    @Input() fileName: string = 'pdfExport.pdf';
    @Input() pageWidth: number = 0;
    @Input() autoScale: boolean = false;

    // Local Properites
    @ViewChild('pdfPrint', { static: true }) pdfPrintRef: PDFExportComponent;
    //@ViewChild('sizeRef', { read: ElementRef }) sizeRef: ElementRef;
    pageToPdfTriggerSubscription: Subscription;

    // Class Functions
    constructor(
        private sharedService: SharedService,
        private spinnerService: SpinnerService
    ) { }    

    // Event Functions
    ngOnInit() {
        this.pageToPdfTriggerSubscription = this.sharedService.observablePageToPdfTrigger$.subscribe(() => {
            this.exportPageToPDF();
        });
    }

    async exportPageToPDF() {        
        this.pdfPrintRef.paperSize = this.paperSize;
        this.pdfPrintRef.margin = this.margin;
        if(!this.autoScale) {
            this.pdfPrintRef.scale = this.scale;
        }
        else {
            if(this.pageWidth < 2000) {
                this.pdfPrintRef.scale = 0.5;
            }
            else if(this.pageWidth < 3000) {
                this.pdfPrintRef.scale = 0.25;
            }
            else {
                this.pdfPrintRef.scale = 0.20;
            }
        }
        this.pdfPrintRef.landscape = this.landscape;
        this.spinnerService.showSpinner = true;                        

        let pdfString: string = '';        

        await this.pdfPrintRef.export().then(async (group: Group) => {        
            await exportPDF(group).then((dataURI) => {      
                pdfString = dataURI;
            })
        });

        const pdfDoc = await PDFDocument.load(pdfString);

        const pdfBase64 = await pdfDoc.saveAsBase64();
    
        System.viewFileFromBase(pdfBase64, 'pdf', this.fileName);   

        setTimeout(() => {
            this.spinnerService.showSpinner = false;
        }, 0);

    }

    ngOnDestroy() {
        // NOTE: this is implemented for @AutoUnsubscribe
    }
    
    // Methods

}
